/**
 * The Runner Handbook — v1.3 (v1.0 approved by Rod 2026-09-11; 1.1 substitution rule 2026-09-27; 1.2 onboarding order 2026-10-02; 1.3 field completion 2026-10-05).
 *
 * GENERATED from docs/driver/runner-handbook-v1.md (EN) and
 * runner-handbook-v1-es.md (ES) — the source of truth is the docs, both
 * languages block-for-block. Regenerate rather than edit here.
 *
 * 🔒 HANDBOOK_VERSION is what the acknowledgement records. Bump it on any
 * material change and every Runner is asked to acknowledge again before their
 * next run (the Terms 2.1 mechanism). A wording fix that changes no rule is
 * not a bump.
 */
export const HANDBOOK_VERSION = '1.3';

export type HandbookLang = 'en' | 'es';
export type HandbookBlock = { type: 'p'; text: string } | { type: 'ul'; items: string[] } | { type: 'ol'; items: string[] };
export interface HandbookSection { title: string; blocks: HandbookBlock[] }
export interface HandbookDoc { sections: HandbookSection[]; footer: string | null }

export const HANDBOOK: Record<HandbookLang, HandbookDoc> = {
 "en": {
  "sections": [
   {
    "title": "What's new in version 1.3",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**Partial picks:** keep available originals and replace only the missing units, following customer preferences.",
       "**Photos:** receipt and sealed, labeled bags per customer; cart photos optional; successful in-person handoffs need no delivery photo.",
       "**Routes and recovery:** review arrival estimates, delivery windows and route warnings; record capture problems and retry saved work after reconnecting."
      ]
     }
    ]
   },
   {
    "title": "What this is",
    "blocks": [
     {
      "type": "p",
      "text": "Grocery List Plus delivery is built on one idea: **the same person delivers to the same households, week after week.** We call our delivery drivers **Runners**. You're not a name in a queue — you carry your own customer book, your customers know you, and you choose each other."
     },
     {
      "type": "p",
      "text": "This handbook is the whole deal, plainly: how you become a Runner, how a delivery run actually works, how the money works — all of it, including our side — and the standards both sides can count on."
     }
    ]
   },
   {
    "title": "Becoming a Runner",
    "blocks": [
     {
      "type": "p",
      "text": "The application has real steps because your customers are trusting you at their door. In order:"
     },
     {
      "type": "ol",
      "items": [
       "**Apply** on our website or in the Runner app — no account needed to start.",
       "**Application review** by a person on our team.",
       "**Profile setup** — your photo (reviewed before it ever shows to a customer), a short bio, languages, the zones you'll serve, how you get around, and how many deliveries a week you want.",
       "**Car insurance** — tell us your insurer, policy number and expiry date, and upload a photo of your insurance card. A person reviews it. Keep it current: we remind you 30, 14, 7 and 3 days before it expires, and with an expired card you can't take new runs (runs already on your schedule still go ahead).",
       "**This handbook** — you'll acknowledge you've read it.",
       "**A 15-minute video call** with our team. Have your photo ID and your insurance card with you: we check the ID matches you and look at the card. We don't keep a copy of your ID.",
       "**Background check** — a criminal and driving-record check through Checkr. If a background check raises a concern, you get the report, a written notice, and at least five business days to respond before any final decision — that's your legal right and we follow it exactly.",
       "**Banking and tax** — payouts run through Stripe; you'll connect an account and complete a W-9. Nobody goes live without being payable.",
       "**Final review, then go live** — you appear to households in your zones the same day."
      ]
     },
     {
      "type": "p",
      "text": "Ongoing: the background check includes continuous monitoring and an annual re-check, and the app will occasionally ask for a quick selfie re-verification — that protects your customers *and* your account from being impersonated. Every change to your profile photo is re-verified against your ID the same way, and the Runner pays for that check — the app shows the amount before you confirm."
     }
    ]
   },
   {
    "title": "How a run works",
    "blocks": [
     {
      "type": "p",
      "text": "**Your schedule is yours.** You offer recurring delivery windows — days, times, zone, and how many deliveries you'll take. Customers order into those windows. Every order has a cutoff, so you always know your run before you start it."
     },
     {
      "type": "p",
      "text": "**A run, start to finish:**"
     },
     {
      "type": "ol",
      "items": [
       "**Run prep.** Review your current orders, customer replies and delivery instructions. Confirm coolers and a charged phone; the shirt check is optional. Review the route before starting.",
       "**Stops.** A collect stop has orders picked and bagged by a vendor: verify the sealed handoff. At a shop stop, shop the customer’s list at the named store. Confirm arrival in the app; if location cannot verify it, record the reason.",
       "**Substitutions (shop stops).** Follow each customer’s shop settings. Keep the original units you can pick and seek a replacement only for missing units. With no substitutions, skip the missing units. With swaps allowed without asking, pick an offered replacement that fits their rules. With ask-me-first, suggest it in the app: the customer has **five minutes** to answer. **No reply means skip the missing units — never guess.** A swap outside their size rules can only be sent as a question; allergy conflicts are blocked. Swap notifications follow the customer’s notification settings.",
       "**Checkout (shop stops).** Each customer gets a separate one-time payment card, armed only for that order, with a buffer above the estimate (10%, at least $5). Anything above that declines. **You never spend your own money.** Payment needs a connection.",
       "**Receipts and bags.** Take receipt photos and a photo of the sealed, labeled bags for **each customer**. A cart photo is optional. Confirm the receipt totals; if receipt reading is unavailable, keep the typed totals and use the app’s review flow. If the camera fails or permission is denied, retry or open Settings. If capture still cannot work, record a meaningful reason for support review; already-paid groceries can continue.",
       "**Route and delivery.** Review estimated arrival times and delivery windows in the app, then use your chosen maps app. Reordering can affect delivery windows and cold groceries; review warnings and keep groceries safe. Location tracking runs only during an active run and stops at completion or sign-out. A successful in-person handoff needs no delivery photo. An unattended delivery needs a photo, or a recorded camera exception for support review. Your pay locks in when the customer confirms, or after 24 hours.",
       "**End of run.** Review your splits — fees, tips and lane pass — on one screen. Saved field actions and photos upload when a connection returns; delivery actions that depend on a photo wait for its upload. Check any sync failure and retry it."
      ]
     },
     {
      "type": "p",
      "text": "**When something goes wrong:** store closed, item missing or damaged, card declined, nobody home — every one has a flow in the app, and using them never counts against you. A store closed on you? You still get a $20 credit for the trip. **If you're ever unsafe: call 911 first, then tell us.** Safety issues are handled by the platform — never by you at a doorstep."
     }
    ]
   },
   {
    "title": "The money — all of it",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**You keep 100% of delivery fees and tips.** We never take a percentage of either. Not at any volume, not ever.",
       "**Our fee is the lane pass:** $2 per delivery, capped at $60 a week. No deliveries, no pass. Your first 30 days are free. Approved time off pauses it.",
       "**That's how we earn on your work — the $2, nothing else on your side.** Flat, so your best weeks are yours.",
       "**Payouts are weekly** by direct deposit. Pay locks in as each customer confirms delivery (or 24 hours pass). The lane pass appears as its own line on every payout statement — you'll never wonder where a dollar went.",
       "**We will never charge you to reach money you already earned.** No paid fast-cash, ever. If early pay arrives as a perk, it will be free and earned by tenure.",
       "**You never front money.** Groceries go on our card. If a register total comes in under the estimate, that difference is ours to absorb, not yours to chase."
      ]
     }
    ]
   },
   {
    "title": "Your customers",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**One Runner per household.** When you and a customer match, you're their Runner — not whoever's closest that day.",
       "**Matching is two-way.** Households can find you; you can browse households looking for a Runner in your zone and invite them. Nobody is assigned.",
       "**Privacy runs both directions.** Until a match is confirmed, you see neighborhoods and delivery windows — never names, addresses, or contact info. The address appears at the first order. Customers see your first name, your reviewed photo, and how you arrive — never your plate, never your number.",
       "**Your windows are commitments.** This is the trade for having your own book: your customers plan their week around your window. Choose your days, your zone, and your capacity — then they count on you for them.",
       "**Time off is built in.** Schedule it in the portal: your customers get about a week's notice, backup coverage is arranged, your lane pass pauses, and your book is intact when you return."
      ]
     }
    ]
   },
   {
    "title": "Communication",
    "blocks": [
     {
      "type": "p",
      "text": "All customer messaging happens **in the app** — no phone numbers change hands, in either direction. It keeps a record both sides can rely on, and it means blocking or reporting a problem is one tap. Messages are bilingual — English and Spanish — and customer notifications respect quiet hours."
     }
    ]
   },
   {
    "title": "Standards",
    "blocks": [
     {
      "type": "p",
      "text": "The model only works because both sides can rely on it, so the bar is explicit."
     },
     {
      "type": "p",
      "text": "**Your schedule is a promise — this is the one we hold hardest.**"
     },
     {
      "type": "p",
      "text": "You choose your days, your windows, your zones and your capacity, and you can change any of them going forward whenever you like. Nobody assigns you hours. That freedom is the whole point — and it's exactly what makes the other half fair. When a delivery lands on a day *you* chose, a household has planned around it: dinner that night, formula, a parent who doesn't drive. They're not refreshing an app hoping someone accepts. They're expecting you, by name."
     },
     {
      "type": "p",
      "text": "So a delivery you accepted and then simply didn't make is the one thing we have close to zero tolerance for. If you can't make it, what matters is **when we hear about it** — and we mean both of us, Grocery List Plus *and* your customer:"
     },
     {
      "type": "ul",
      "items": [
       "**Days ahead:** change your schedule yourself. Nothing owed, nobody affected.",
       "**The day before:** tell us and your customer. We move the window or cover the run.",
       "**Same day:** call support — don't just message. There's less time to cover it.",
       "**Going quiet:** this is the one that ends a Runner relationship."
      ]
     },
     {
      "type": "p",
      "text": "**What is never a broken promise.** An accident. A door that's genuinely unsafe. A weather emergency that hits while you're already out. Getting sick. You will never be penalised for refusing to walk into danger — what we need in those moments is the phone call, not the risk. Get safe, then tell us straight away so we can re-route and let the household know."
     },
     {
      "type": "p",
      "text": "The rest of the bar:"
     },
     {
      "type": "ul",
      "items": [
       "**Trusted Runner** status comes at 30 days, 50 deliveries, and 95% satisfaction — it's visible to customers.",
       "**Complaints:** three confirmed complaints in 30 days brings a review; five, a suspension.",
       "**Theft is different:** one confirmed incident ends it, immediately.",
       "Every removal decision is made by a person, with a record."
      ]
     }
    ]
   },
   {
    "title": "Your business",
    "blocks": [
     {
      "type": "p",
      "text": "Runners are **independent contractors**. You run your own operation — we're the platform it runs on. Everyone gives us a W-9 before go-live; whether you get a 1099-NEC at year end follows what that W-9 says (sole proprietors and most LLCs do; an LLC taxed as an S- or C-corp doesn't — same independent status either way). Two things we ask you to have squared away, and one thing we built to help:"
     },
     {
      "type": "ul",
      "items": [
       "**Insurance.** You'll upload your insurance card and keep it current. Know this one: many personal auto policies exclude delivery driving — ask your insurer about a delivery endorsement *before* your first run.",
       "**Taxes.** Nobody withholds for you. Set money aside from every payout.",
       "**The Runner's Kit** — free in your portal and in this app: business setup guides, taxes 101, a mileage log, a quarterly-tax estimator, an expense tracker, and a first-30-days checklist. It's guidance, not tax or legal advice — confirm anything that matters with a licensed accountant or attorney."
      ]
     }
    ]
   }
  ],
  "footer": "This handbook is part of your onboarding acknowledgment. Your acknowledgement is recorded with the handbook version and the time. When it changes materially, you'll see what changed — not just a new date — and you'll be asked to acknowledge it again before your next run."
 },
 "es": {
  "sections": [
   {
    "title": "Novedades de la versión 1.3",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**Compras parciales:** conserva los originales disponibles y reemplaza solo las unidades faltantes según las preferencias del cliente.",
       "**Fotos:** recibo y bolsas selladas y etiquetadas por cliente; carrito opcional; las entregas en persona realizadas con éxito no necesitan foto.",
       "**Rutas y recuperación:** revisa las estimaciones, ventanas y advertencias; registra los problemas de captura y reintenta lo guardado al recuperar la conexión."
      ]
     }
    ]
   },
   {
    "title": "Qué es esto",
    "blocks": [
     {
      "type": "p",
      "text": "Las entregas de Lista de Compras Plus se basan en una idea: **la misma persona entrega a los mismos hogares, semana tras semana.** A nuestros repartidores los llamamos **Runners**. No eres un nombre en una cola: tienes tu propia cartera de clientes, tus clientes te conocen y ustedes se eligen mutuamente."
     },
     {
      "type": "p",
      "text": "Este manual es el trato completo, en claro: cómo te conviertes en Runner, cómo funciona una ruta de entrega en la práctica, cómo funciona el dinero — todo, incluida nuestra parte — y las normas con las que ambas partes pueden contar."
     }
    ]
   },
   {
    "title": "Cómo convertirte en Runner",
    "blocks": [
     {
      "type": "p",
      "text": "La solicitud tiene pasos de verdad porque tus clientes confían en ti en su puerta. En orden:"
     },
     {
      "type": "ol",
      "items": [
       "**Solicita** en nuestro sitio web o en la app de Runner: no hace falta cuenta para empezar.",
       "**Revisión de la solicitud** por una persona de nuestro equipo.",
       "**Configuración del perfil**: tu foto (revisada antes de que la vea cualquier cliente), una breve biografía, idiomas, las zonas que atenderás, cómo te desplazas y cuántas entregas por semana quieres.",
       "**Seguro del auto**: indícanos tu aseguradora, el número de póliza y la fecha de vencimiento, y sube una foto de tu tarjeta del seguro. Una persona la revisa. Mantenlo vigente: te avisamos 30, 14, 7 y 3 días antes de que venza, y con la tarjeta vencida no puedes aceptar rutas nuevas (las rutas que ya tienes en tu horario siguen adelante).",
       "**Este manual**: reconocerás que lo has leído.",
       "**Una videollamada de 15 minutos** con nuestro equipo. Ten a mano tu identificación con foto y tu tarjeta del seguro: comprobamos que la identificación coincide contigo y revisamos la tarjeta. No guardamos copia de tu identificación.",
       "**Verificación de antecedentes**: una revisión de antecedentes penales y de historial de conducción a través de Checkr. Si la verificación de antecedentes plantea una duda, recibes el informe, un aviso por escrito y al menos cinco días hábiles para responder antes de cualquier decisión final: es tu derecho legal y lo cumplimos al pie de la letra.",
       "**Banco e impuestos**: los pagos se hacen a través de Stripe; conectarás una cuenta y completarás un W-9. Nadie se activa sin poder recibir pagos.",
       "**Revisión final y activación**: apareces para los hogares de tus zonas el mismo día."
      ]
     },
     {
      "type": "p",
      "text": "Después: la verificación de antecedentes incluye monitoreo continuo y una nueva revisión anual, y la app te pedirá de vez en cuando una rápida re-verificación con selfie: eso protege a tus clientes *y* a tu cuenta de suplantaciones. Cada cambio de tu foto de perfil se re-verifica contra tu identificación de la misma manera, y el Runner paga esa comprobación: la app muestra el importe antes de que confirmes."
     }
    ]
   },
   {
    "title": "Cómo funciona una ruta",
    "blocks": [
     {
      "type": "p",
      "text": "**Tu horario es tuyo.** Ofreces ventanas de entrega recurrentes: días, horas, zona y cuántas entregas aceptarás. Los clientes piden dentro de esas ventanas. Cada pedido tiene una hora de cierre, así que siempre conoces tu ruta antes de empezarla."
     },
     {
      "type": "p",
      "text": "**Una ruta, de principio a fin:**"
     },
     {
      "type": "ol",
      "items": [
       "**Preparación de la ruta.** Revisa los pedidos actuales, las respuestas de los clientes y las instrucciones de entrega. Confirma las hieleras y el teléfono cargado; la verificación de la camiseta es opcional. Revisa la ruta antes de empezar.",
       "**Paradas.** En una parada de recogida, un vendedor preparó y embolsó los pedidos: verifica la entrega sellada. En una parada de compra, compra la lista del cliente en la tienda indicada. Confirma la llegada en la app; si la ubicación no permite verificarla, registra el motivo.",
       "**Sustituciones (paradas de compra).** Sigue la configuración de cada cliente. Conserva las unidades originales disponibles y busca un reemplazo solo para las faltantes. Sin sustituciones, omite las unidades faltantes. Con cambios permitidos sin preguntar, elige un reemplazo ofrecido que cumpla sus reglas. Con preguntarme primero, sugiérelo en la app: el cliente tiene **cinco minutos** para responder. **Sin respuesta, omite las unidades faltantes; nunca adivines.** Un cambio fuera de las reglas de tamaño solo se puede enviar como pregunta; los conflictos de alergias quedan bloqueados. Los avisos de cambios siguen la configuración de notificaciones del cliente.",
       "**Pago en caja (paradas de compra).** Cada cliente recibe una tarjeta de pago independiente y de un solo uso, activada solo para ese pedido, con un margen por encima del estimado (10%, mínimo $5). Cualquier importe superior se rechaza. **Nunca gastas tu propio dinero.** El pago necesita conexión.",
       "**Recibos y bolsas.** Toma fotos del recibo y una foto de las bolsas selladas y etiquetadas para **cada cliente**. La foto del carrito es opcional. Confirma los totales del recibo; si la lectura no está disponible, conserva los totales escritos y usa el flujo de revisión de la app. Si la cámara falla o no tiene permiso, reintenta o abre Ajustes. Si sigue sin funcionar, registra un motivo claro para revisión por soporte; las compras ya pagadas pueden continuar.",
       "**Ruta y entrega.** Revisa las horas estimadas de llegada y las ventanas de entrega en la app y usa tu app de mapas elegida. Cambiar el orden puede afectar las ventanas y los productos fríos; revisa las advertencias y conserva las compras de forma segura. El seguimiento de ubicación funciona solo durante una ruta activa y termina al completarla o cerrar sesión. Una entrega en persona realizada con éxito no necesita foto. Una entrega sin presencia del cliente necesita foto o una excepción de cámara registrada para revisión por soporte. Tu pago queda fijado cuando el cliente confirma o pasadas 24 horas.",
       "**Fin de la ruta.** Revisa tus parciales: tarifas, propinas y pase de carril, en una pantalla. Las acciones y fotos guardadas se suben al recuperar la conexión; las entregas que dependen de una foto esperan a que se suba. Revisa cualquier fallo de sincronización y reinténtalo."
      ]
     },
     {
      "type": "p",
      "text": "**Cuando algo sale mal:** tienda cerrada, artículo faltante o dañado, tarjeta rechazada, nadie en casa: cada caso tiene su flujo en la app, y usarlos nunca cuenta en tu contra. ¿Una tienda te cerró? Igual recibes un crédito de $20 por el viaje. **Si alguna vez estás en peligro: llama primero al 911 y luego avísanos.** Los problemas de seguridad los gestiona la plataforma, nunca tú en una puerta."
     }
    ]
   },
   {
    "title": "El dinero — todo",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**Te quedas con el 100% de las tarifas de entrega y de las propinas.** Nunca nos quedamos con un porcentaje de ninguna de las dos. A ningún volumen, nunca.",
       "**Nuestra tarifa es el pase de carril:** $2 por entrega, con un tope de $60 por semana. Sin entregas, sin pase. Tus primeros 30 días son gratis. Los descansos aprobados lo pausan.",
       "**Así ganamos con tu trabajo: los $2, nada más de tu lado.** Fijo, para que tus mejores semanas sean tuyas.",
       "**Los pagos son semanales** por depósito directo. El pago se fija a medida que cada cliente confirma la entrega (o pasan 24 horas). El pase de carril aparece como su propia línea en cada estado de pago: nunca te preguntarás a dónde fue un dólar.",
       "**Nunca te cobraremos por acceder a dinero que ya ganaste.** Nada de pagos rápidos con cargo, nunca. Si llega un pago anticipado como beneficio, será gratis y ganado por antigüedad.",
       "**Nunca adelantas dinero.** Las compras van en nuestra tarjeta. Si el total de caja sale por debajo del estimado, esa diferencia la absorbemos nosotros; no es tuya de perseguir."
      ]
     }
    ]
   },
   {
    "title": "Tus clientes",
    "blocks": [
     {
      "type": "ul",
      "items": [
       "**Un Runner por hogar.** Cuando tú y un cliente hacen match, eres su Runner; no quien esté más cerca ese día.",
       "**El emparejamiento es en ambos sentidos.** Los hogares pueden encontrarte; tú puedes ver hogares que buscan Runner en tu zona e invitarlos. Nadie es asignado.",
       "**La privacidad va en ambas direcciones.** Hasta que se confirma un match, ves vecindarios y ventanas de entrega, nunca nombres, direcciones ni datos de contacto. La dirección aparece con el primer pedido. Los clientes ven tu nombre, tu foto revisada y cómo llegas: nunca tu placa, nunca tu número.",
       "**Tus ventanas son compromisos.** Es el intercambio por tener tu propia cartera: tus clientes planifican su semana alrededor de tu ventana. Elige tus días, tu zona y tu capacidad, y luego cuentan contigo para ello.",
       "**Los descansos están previstos.** Prográmalos en el portal: tus clientes reciben aviso con alrededor de una semana, se organiza cobertura de respaldo, tu pase de carril se pausa y tu cartera está intacta cuando vuelves."
      ]
     }
    ]
   },
   {
    "title": "Comunicación",
    "blocks": [
     {
      "type": "p",
      "text": "Toda la mensajería con clientes ocurre **en la app**: no se intercambian números de teléfono, en ninguna dirección. Deja un registro en el que ambas partes pueden confiar, y hace que bloquear o reportar un problema sea un solo toque. Los mensajes son bilingües, inglés y español, y las notificaciones a clientes respetan las horas de silencio."
     }
    ]
   },
   {
    "title": "Normas",
    "blocks": [
     {
      "type": "p",
      "text": "El modelo solo funciona porque ambas partes pueden confiar en él, así que el listón es explícito."
     },
     {
      "type": "p",
      "text": "**Tu horario es una promesa; es la que más exigimos.**"
     },
     {
      "type": "p",
      "text": "Eliges tus días, tus ventanas, tus zonas y tu capacidad, y puedes cambiarlos hacia adelante cuando quieras. Nadie te asigna horas. Esa libertad es todo el sentido, y es exactamente lo que hace justa la otra mitad. Cuando una entrega cae en un día que *tú* elegiste, un hogar planificó alrededor de ella: la cena de esa noche, la fórmula, un padre que no conduce. No están actualizando una app esperando que alguien acepte. Te esperan a ti, por tu nombre."
     },
     {
      "type": "p",
      "text": "Así que una entrega que aceptaste y simplemente no hiciste es lo único con lo que tenemos casi tolerancia cero. Si no puedes hacerla, lo que importa es **cuándo nos enteramos**, y nos referimos a los dos, Lista de Compras Plus *y* tu cliente:"
     },
     {
      "type": "ul",
      "items": [
       "**Con días de antelación:** cambia tu horario tú mismo. No debes nada, nadie se ve afectado.",
       "**El día anterior:** avísanos a nosotros y a tu cliente. Movemos la ventana o cubrimos la ruta.",
       "**El mismo día:** llama a soporte, no solo mensaje. Hay menos tiempo para cubrirla.",
       "**Desaparecer:** esta es la que termina la relación con un Runner."
      ]
     },
     {
      "type": "p",
      "text": "**Lo que nunca es una promesa rota.** Un accidente. Una puerta realmente insegura. Una emergencia climática que llega cuando ya estás fuera. Enfermarte. Nunca serás penalizado por negarte a entrar en peligro; lo que necesitamos en esos momentos es la llamada, no el riesgo. Ponte a salvo y luego avísanos de inmediato para poder reenrutar y avisar al hogar."
     },
     {
      "type": "p",
      "text": "El resto del listón:"
     },
     {
      "type": "ul",
      "items": [
       "El estatus de **Runner de Confianza** llega a los 30 días, 50 entregas y 95% de satisfacción; los clientes lo ven.",
       "**Quejas:** tres quejas confirmadas en 30 días llevan a una revisión; cinco, a una suspensión.",
       "**El robo es distinto:** un incidente confirmado lo termina, de inmediato.",
       "Cada decisión de baja la toma una persona, con registro."
      ]
     }
    ]
   },
   {
    "title": "Tu negocio",
    "blocks": [
     {
      "type": "p",
      "text": "Los Runners son **contratistas independientes**. Diriges tu propia operación; nosotros somos la plataforma sobre la que funciona. Todos nos entregan un W-9 antes de activarse; que recibas un 1099-NEC a fin de año depende de lo que diga ese W-9 (los propietarios únicos y la mayoría de las LLC sí; una LLC que tributa como S-corp o C-corp no; la misma condición de independiente en ambos casos). Dos cosas que te pedimos tener resueltas, y una que construimos para ayudar:"
     },
     {
      "type": "ul",
      "items": [
       "**Seguro.** Subirás tu tarjeta del seguro y la mantendrás vigente. Ten esto claro: muchas pólizas de auto personales excluyen el reparto; pregunta a tu aseguradora por un endoso de reparto *antes* de tu primera ruta.",
       "**Impuestos.** Nadie retiene por ti. Aparta dinero de cada pago.",
       "**El Kit del Runner**: gratis en tu portal y en esta app: guías para montar tu negocio, impuestos 101, un registro de millas, un estimador de impuestos trimestrales, un registro de gastos y una lista de los primeros 30 días. Es orientación, no asesoría fiscal ni legal: confirma cualquier cosa importante con un contador o abogado con licencia."
      ]
     }
    ]
   }
  ],
  "footer": "Este manual forma parte de tu reconocimiento de incorporación. Tu reconocimiento se\nregistra con la versión del manual y la hora. Cuando cambie de forma sustancial, verás\nqué cambió — no solo una fecha nueva — y se te pedirá reconocerlo de nuevo antes de tu\nsiguiente ruta."
 }
};
